/**
 * Puts Bold's quantity break prices on the same VAT basis as every other price on the page.
 *
 * Every price the theme renders goes through snippets/price-as-money.liquid, which writes both
 * bases into the markup and lets CSS show one from data-vat-mode. Bold's grid cannot: its script
 * writes the table after the page has rendered, as plain text inside <span class="money">, so
 * Liquid never sees those numbers. Without this the table sat on one basis while the price above
 * it sat on the other.
 *
 * Same job, done in the browser. Each amount becomes an incl and an excl span, and the CSS that
 * already keys off data-vat-mode shows one of them - so the switch needs no extra wiring, and
 * the grid changes basis in the same paint as the rest of the page.
 *
 * Bold prints the raw Shopify price, so which basis it starts from is the shop's own tax
 * setting, passed in as theme.settings.taxesIncluded rather than assumed.
 *
 * Only the digits are replaced. The surrounding text - currency, spacing, "stk." - is Bold's and
 * is left exactly as written, so nothing here has to know how money is formatted in this market.
 */
(function () {
  'use strict';

  if (window.ZimpleBoldVat) return;

  /* Written on each span once converted, so a re-run cannot convert the same amount twice. */
  const DONE = 'data-vat-done';

  /**
   * @returns {?{rate: number, included: boolean}} Null when there is nothing to convert with.
   */
  function readSettings() {
    const s = (window.theme && window.theme.settings) || {};
    const rate = Number(s.vatRate);

    if (!Number.isFinite(rate) || rate <= 0) return null;

    return { rate, included: s.taxesIncluded === true };
  }

  /**
   * Pulls the number out of a formatted amount, along with the shape it was written in, so the
   * converted figure can be put back the same way.
   *
   * @param {string} text For example "8.446,25 kr" or "1,234.56 USD".
   * @returns {?{value: number, raw: string, decimal: ?string, group: ?string}}
   */
  function parseAmount(text) {
    const match = text.match(/\d[\d.,   ]*\d|\d/);
    if (!match) return null;

    const raw = match[0];
    /* Thin and non-breaking spaces are grouping characters in some locales. */
    const compact = raw.replace(/[   ]/g, '');

    /* The separator three characters from the end, with two digits after it, is the decimal
       one. Anything else that looks like a separator groups thousands. */
    const decimal = /[.,]\d{2}$/.test(compact) ? compact.charAt(compact.length - 3) : null;

    let group = null;
    if (decimal) {
      group = decimal === ',' ? '.' : ',';
    } else {
      const found = compact.match(/[.,]/);
      if (found) [group] = found;
    }

    let digits = compact;
    if (group) digits = digits.split(group).join('');
    if (decimal) digits = `${digits.slice(0, -3)}.${digits.slice(-2)}`;

    const value = parseFloat(digits);
    if (!Number.isFinite(value)) return null;

    return { value, raw, decimal, group };
  }

  /**
   * @param {number} value
   * @param {{decimal: ?string, group: ?string}} shape From parseAmount.
   * @returns {string}
   */
  function formatAmount(value, shape) {
    const fixed = value.toFixed(shape.decimal ? 2 : 0);
    const parts = fixed.split('.');
    let whole = parts[0];

    if (shape.group) {
      whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, shape.group);
    }

    return parts[1] ? whole + shape.decimal + parts[1] : whole;
  }

  /**
   * @param {number} value
   * @param {{rate: number, included: boolean}} settings
   * @returns {{incl: number, excl: number}}
   */
  function convert(value, settings) {
    const factor = 1 + settings.rate / 100;

    return settings.included
      ? { incl: value, excl: value / factor }
      : { incl: value * factor, excl: value };
  }

  /**
   * Replaces one amount with the pair of bases.
   * @param {Element} el A span Bold wrote an amount into.
   * @param {{rate: number, included: boolean}} settings
   */
  function rewrite(el, settings) {
    const text = el.textContent;
    const parsed = parseAmount(text);
    if (!parsed) return;

    const amounts = convert(parsed.value, settings);
    const shape = { decimal: parsed.decimal, group: parsed.group };

    const basisSpan = (basis) => {
      const span = document.createElement('span');
      span.setAttribute('data-vat', basis);
      /* Only the first occurrence, which is the amount that was matched. */
      span.textContent = text.replace(parsed.raw, formatAmount(amounts[basis], shape));
      return span;
    };

    el.textContent = '';
    el.appendChild(basisSpan('incl'));
    el.appendChild(basisSpan('excl'));
    el.setAttribute(DONE, '');
  }

  /**
   * @param {Element} root A grid, or the document.
   */
  function convertWithin(root) {
    const settings = readSettings();
    if (!settings) return;

    root.querySelectorAll(`.money:not([${DONE}])`).forEach((el) => rewrite(el, settings));
  }

  function start() {
    const grids = document.querySelectorAll('.bold_qb_grid');
    if (!grids.length) return;

    grids.forEach((grid) => {
      convertWithin(grid);

      /* Bold fills the grid after this runs, and refills it on a variant change. The guard
         attribute keeps our own insertions from setting the observer off again. */
      new MutationObserver(() => convertWithin(grid)).observe(grid, {
        childList: true,
        subtree: true
      });
    });
  }

  window.ZimpleBoldVat = { parseAmount, formatAmount, convert };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
